import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Phone } from 'lucide-react';
import { vdDepartmentApi } from '../api';

export default function VDDepartments() {
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { register, handleSubmit, reset, watch, setValue } = useForm();

  const load = () => vdDepartmentApi.list().then(d => { setDepts(d); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); reset({ name: '', extension: '', forwardNumber: '', voicemailEnabled: false, active: true }); setDialogOpen(true); };
  const openEdit = (dept) => { setEditing(dept); reset(dept); setDialogOpen(true); };

  const onSubmit = async (data) => {
    if (editing) {
      await vdDepartmentApi.update(editing.id, data);
      toast.success('Department updated');
    } else {
      await vdDepartmentApi.create(data);
      toast.success('Department added');
    }
    setDialogOpen(false);
    load();
  };

  const onDelete = async (id) => {
    await vdDepartmentApi.remove(id);
    toast.success('Department removed');
    load();
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="vd-departments">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Departments</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage your company departments for call routing</p>
        </div>
        <Button onClick={openNew} className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium" data-testid="add-department-btn">
          <Plus className="w-4 h-4 mr-1.5" />Add Department
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-card/5 rounded-xl animate-pulse" />)}</div>
      ) : depts.length === 0 ? (
        <Card className="bg-card/5 border-white/10">
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Phone className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">No departments yet</p>
            <Button variant="outline" size="sm" className="mt-3 border-white/10" onClick={openNew}>Add your first department</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {depts.map(dept => (
            <Card key={dept.id} className="bg-card/5 border-white/10">
              <CardContent className="flex items-center justify-between py-3 px-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 text-sm font-bold shrink-0">
                    {dept.extension || '#'}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{dept.name}</div>
                    <div className="text-xs text-muted-foreground truncate">Ext {dept.extension} &middot; {dept.forwardNumber || 'No forward'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={dept.active ? 'border-emerald-500/30 text-emerald-400' : 'border-border text-muted-foreground'}>{dept.active ? 'Active' : 'Inactive'}</Badge>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(dept)} className="text-muted-foreground hover:text-foreground h-8 w-8"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(dept.id)} className="text-muted-foreground hover:text-red-400 h-8 w-8"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1a1d2e] border-white/10 text-foreground">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Department</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div><Label>Name</Label><Input {...register('name', { required: true })} className="bg-card/5 border-white/10 mt-1" placeholder="Sales, Support, Billing..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Extension</Label><Input {...register('extension')} className="bg-card/5 border-white/10 mt-1" placeholder="100" /></div>
              <div><Label>Forward Number</Label><Input {...register('forwardNumber')} className="bg-card/5 border-white/10 mt-1" placeholder="+1..." /></div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Voicemail Enabled</Label>
              <Switch checked={!!watch('voicemailEnabled')} onCheckedChange={v => setValue('voicemailEnabled', v)} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={!!watch('active')} onCheckedChange={v => setValue('active', v)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Cancel</Button>
              <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600 text-black">{editing ? 'Save' : 'Add'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
